'use client';

import { Fragment, type ReactNode, startTransition, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/ui/accordion';
import { Divider } from '@/components/ui/divider';
import { ExpandMoreIcon } from '@/components/ui/icons';
import { ListFilterDTO } from '@/features/filters/types/filters.dto';
import { FilterCheckboxRow } from '@/features/products/components/list/filters/filterCheckboxRow';
import { RatingProductsFilter } from '@/features/products/components/list/filters/ratingFilter/ratingProductsFilter';
import { RatingFilterLabel } from '@/features/products/components/list/filters/ratingFilterLabel';
import { productListUpdateFilters } from '@/features/products/state/list/actions';
import {
  useProductListDispatch,
  useProductListSelectedFilters
} from '@/features/products/state/list/context';
import { FilterLabel } from '@/features/products/types/filterLabel';
import { listingTranslate } from '@/i18n/translations/listingTranslations';
import getScreenSize from '@/lib/getScreenSize';

interface ProductListFilterAccordionProps extends ListFilterDTO {
  expanded?: boolean;
  filter?: ListFilterDTO;
  onFilterChange?: (code: string, filterCode: string) => void;
  renderFilterLabel?: (FilterLabel: FilterLabel) => ReactNode;
}

export const ProductFiltersAccordion = ({
  title,
  items,
  code,
  expanded,
  filter,
  onFilterChange,
  renderFilterLabel
}: ProductListFilterAccordionProps) => {
  const [showAll, setShowAll] = useState(false);
  const dispatch = useProductListDispatch();
  const selectedFilters = useProductListSelectedFilters();
  const { isMobileView } = getScreenSize();
  const displayedFilters = showAll ? items : items?.slice(0, 5);

  const handleFiltersChange = (filterCode: string) => {
    // The reducer keeps a single-value voivodeship on a repeat click; '' is what deselects it.
    const isVoivodeshipDeselect =
      code === 'voivodeship' && !!selectedFilters[code]?.includes(filterCode);
    startTransition(() => {
      dispatch(
        productListUpdateFilters({
          filterCategoryCode: code,
          code: isVoivodeshipDeselect ? '' : filterCode
        })
      );
      if (onFilterChange) {
        onFilterChange(code, filterCode);
      }
    });
  };

  const handleShowMore = () => {
    setShowAll(true);
  };

  if (!items.length) return;

  return (
    <Fragment>
      <Accordion
        className="shadow-none border-none before:hidden bg-transparent mt-2"
        defaultExpanded={
          typeof expanded === 'boolean'
            ? expanded
            : isMobileView || code === 'minRating'
              ? false
              : true
        }
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-(--mui-palette-primary-main)" />}
          className="border-none"
        >
          <span className="font-bold">{title}</span>
        </AccordionSummary>
        <AccordionDetails>
          {code === 'minRating' ? (
            <RatingProductsFilter
              title={title}
              items={items}
              code={code}
              expanded={false}
              renderFilterLabel={({ filter, children }) => {
                return <RatingFilterLabel filter={filter}>{children}</RatingFilterLabel>;
              }}
            />
          ) : (
            displayedFilters?.map((filter) => (
              <FilterCheckboxRow
                key={filter.code}
                filter={filter}
                checked={selectedFilters[code]?.includes(filter.code) || false}
                onToggle={handleFiltersChange}
                renderFilterLabel={renderFilterLabel}
              />
            ))
          )}
          {!showAll && items && items.length > 5 && (
            <p
              onClick={handleShowMore}
              className="mt-2 text-center cursor-pointer text-sm text-(--mui-palette-primary-main)"
            >
              {listingTranslate('filters.more')} ({items.length - 5})
            </p>
          )}
        </AccordionDetails>
      </Accordion>
      <Divider className="mx-4" />
    </Fragment>
  );
};
